import { proxy } from 'valtio';
import type { User, CompanyMembership } from 'shared/types/auth';

// ─── Types ─────────────────────────────────────────────────────
interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  companies: CompanyMembership[];
  isInitialised: boolean;
  isLoading: boolean;
  isSwitchingCompany: boolean;
}

// ─── State ─────────────────────────────────────────────────────
export const authState = proxy<AuthState>({
  user: null,
  accessToken: null,
  refreshToken: null,
  companies: [],
  isInitialised: false,
  isLoading: false,
  isSwitchingCompany: false,
});

// ─── Persistence helpers ───────────────────────────────────────
const REFRESH_TOKEN_KEY = 'refreshToken';

function persistRefreshToken(token: string | null) {
  if (token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
}

function getStoredRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

// ─── API base URL ──────────────────────────────────────────────
const API_BASE = '/api';

async function authFetch(path: string, options: RequestInit = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
      ...(authState.accessToken
        ? { Authorization: `Bearer ${authState.accessToken}` }
        : {}),
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(body.error || body.message || `HTTP ${res.status}`) as Error & {
      status: number;
      details?: string[];
    };
    err.status = res.status;
    if (body.details) err.details = body.details;
    throw err;
  }

  return res.json();
}

// ─── Actions ───────────────────────────────────────────────────
export const authActions = {
  /**
   * Try to restore a session from a stored refresh token on app startup.
   * Always sets isInitialised = true when done (even on failure).
   */
  async initialise(): Promise<void> {
    const storedToken = getStoredRefreshToken();
    if (!storedToken) {
      authState.isInitialised = true;
      return;
    }

    authState.isLoading = true;
    try {
      const data = await authFetch('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: storedToken }),
      });
      authState.accessToken = data.accessToken;
      authState.refreshToken = data.refreshToken;
      authState.user = data.user;
      authState.companies = data.companies || [];
      persistRefreshToken(data.refreshToken);
    } catch {
      // Stored token is invalid or expired — user needs to log in again
      persistRefreshToken(null);
      authState.accessToken = null;
      authState.refreshToken = null;
      authState.user = null;
      authState.companies = [];
    } finally {
      authState.isInitialised = true;
      authState.isLoading = false;
    }
  },

  /**
   * Public unauthenticated config — surfaces feature flags to the login UI.
   * Returns { enhancedAuthEnabled, smsEnabled, becertainEnabled }. Cached
   * locally per session; the page's useEffect re-fetches on mount so a flag
   * flip in DigitalOcean is picked up on the next page load.
   */
  async getAuthConfig(): Promise<{
    enhancedAuthEnabled: boolean;
    smsEnabled: boolean;
    becertainEnabled: boolean;
    becertainAutoCreateCompanies?: boolean;
    demoEnabled?: boolean;
  }> {
    return await authFetch('/auth/config');
  },

  // ─── Certain (BeCertain) SSO ────────────────────────────────────────
  // Three-step proxy: kick off → poll until BeCertain has shipped the SMS →
  // verify the OTP and receive tokens.

  async startCertainLogin(email: string, password: string): Promise<void> {
    await authFetch('/auth/becertain/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  async pollCertainLoginStatus(email: string): Promise<{ ready: boolean; error?: string }> {
    return await authFetch('/auth/becertain/login-status', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Verify the SMS code against Certain. Same response shape as
   * /api/auth/login/verify so the caller branches on `nextStep`.
   */
  async verifyCertainOtp(email: string, otp: string): Promise<
    | {
        nextStep: 'tokens';
        accessToken: string;
        refreshToken: string;
        user: User;
        companies: CompanyMembership[];
      }
    | {
        nextStep: 'select-tenant';
        selectionToken: string;
        companies: CompanyMembership[];
      }
  > {
    const data = await authFetch('/auth/becertain/verify', {
      method: 'POST',
      body: JSON.stringify({ email, otp }),
    });
    if (data.nextStep === 'tokens') {
      authState.accessToken = data.accessToken;
      authState.refreshToken = data.refreshToken;
      authState.user = data.user;
      authState.companies = data.companies || [];
      persistRefreshToken(data.refreshToken);
    }
    return data;
  },

  /**
   * Native-auth step 1 — submit email + password, receive a challenge token
   * and either an SMS OTP (method='sms') or a hint to fetch the code from
   * the user's authenticator app (method='totp', no SMS sent).
   *
   * Throws a typed Error on lockout, no-factor, invalid creds, etc., so the
   * page can surface the right copy.
   */
  async startLogin(email: string, password: string): Promise<{
    challengeToken: string;
    method: 'sms' | 'totp';
    maskedPhone: string | null;
    mustChangePassword: boolean;
  }> {
    return await authFetch('/auth/login/start', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  /**
   * Native-auth step 2 — present the 6-digit code. Server returns one of
   * three branches:
   *   - tokens (single-tenant) — caller stores them and is signed in
   *   - select-tenant (multi-tenant) — caller shows a workspace picker
   *   - reset-password (must_change_password) — caller redirects to /reset-password
   */
  async verifyLoginOtp(challengeToken: string, otp: string): Promise<
    | {
        nextStep: 'tokens';
        accessToken: string;
        refreshToken: string;
        user: User;
        companies: CompanyMembership[];
      }
    | {
        nextStep: 'select-tenant';
        selectionToken: string;
        companies: CompanyMembership[];
      }
    | {
        nextStep: 'reset-password';
        resetToken: string;
      }
  > {
    const data = await authFetch('/auth/login/verify', {
      method: 'POST',
      body: JSON.stringify({ challengeToken, otp }),
    });

    // Single-tenant happy path — commit tokens to the store immediately so
    // any code reading authState (and thus making authenticated requests)
    // works on the next render.
    if (data.nextStep === 'tokens') {
      authState.accessToken = data.accessToken;
      authState.refreshToken = data.refreshToken;
      authState.user = data.user;
      authState.companies = data.companies || [];
      persistRefreshToken(data.refreshToken);
    }
    return data;
  },

  /**
   * Native-auth step 3 — multi-tenant workspace picker. Caller passes the
   * selection token they got from step 2 plus the chosen company id; server
   * returns access + refresh tokens scoped to that tenant.
   */
  async selectLoginTenant(selectionToken: string, companyId: string): Promise<void> {
    const data = await authFetch('/auth/login/select-tenant', {
      method: 'POST',
      body: JSON.stringify({ selectionToken, companyId }),
    });
    authState.accessToken = data.accessToken;
    authState.refreshToken = data.refreshToken;
    authState.user = data.user;
    authState.companies = data.companies || [];
    persistRefreshToken(data.refreshToken);
  },

  /**
   * Log in with email + password (LEGACY single-step flow).
   * Kept untouched for the flag-off path. The new flow lives in
   * `startLogin` + `verifyLoginOtp` + `selectLoginTenant` above.
   */
  async login(email: string, password: string): Promise<void> {
    authState.isLoading = true;
    try {
      const data = await authFetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      authState.accessToken = data.accessToken;
      authState.refreshToken = data.refreshToken;
      authState.user = data.user;
      authState.companies = data.companies || [];
      persistRefreshToken(data.refreshToken);
    } finally {
      authState.isLoading = false;
    }
  },

  /**
   * Password-less sign-in to the North Wind demo tenant. Backed by
   * `/auth/demo`, enabled only when the server reports `demoEnabled: true`
   * on `/auth/config`. Mirrors the shape of `login` so the LoginPage can
   * treat it as "signed in" with no extra UX branching.
   */
  async demoSignin(variant: 'admin' | 'member'): Promise<void> {
    authState.isLoading = true;
    try {
      const data = await authFetch('/auth/demo', {
        method: 'POST',
        body: JSON.stringify({ variant }),
      });
      authState.accessToken = data.accessToken;
      authState.refreshToken = data.refreshToken;
      authState.user = data.user;
      authState.companies = data.companies || [];
      persistRefreshToken(data.refreshToken);
    } finally {
      authState.isLoading = false;
    }
  },

  /**
   * Register a new company and admin user.
   */
  async register(payload: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    companyName: string;
  }): Promise<void> {
    authState.isLoading = true;
    try {
      const data = await authFetch('/auth/register', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      authState.accessToken = data.accessToken;
      authState.refreshToken = data.refreshToken;
      authState.user = data.user;
      authState.companies = data.companies || [];
      persistRefreshToken(data.refreshToken);
    } finally {
      authState.isLoading = false;
    }
  },

  /**
   * Accept an invite and create a new user account.
   */
  async acceptInvite(payload: {
    token: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }): Promise<void> {
    authState.isLoading = true;
    try {
      const data = await authFetch('/auth/invite/accept', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      authState.accessToken = data.accessToken;
      authState.refreshToken = data.refreshToken;
      authState.user = data.user;
      authState.companies = data.companies || [];
      persistRefreshToken(data.refreshToken);
    } finally {
      authState.isLoading = false;
    }
  },

  /**
   * Request a password reset email.
   */
  async requestPasswordReset(email: string): Promise<string> {
    const data = await authFetch('/auth/password-reset/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
    return data.message;
  },

  /**
   * Confirm a password reset with a new password.
   */
  async confirmPasswordReset(token: string, newPassword: string): Promise<string> {
    const data = await authFetch('/auth/password-reset/confirm', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
    return data.message;
  },

  /**
   * Get invite info before accepting.
   */
  async getInviteInfo(token: string) {
    return authFetch(`/auth/invite/info?token=${encodeURIComponent(token)}`);
  },

  /**
   * Refresh the access token using the stored refresh token.
   * Returns the new access token, or null if refresh failed.
   */
  async refreshAccessToken(): Promise<string | null> {
    const token = authState.refreshToken || getStoredRefreshToken();
    if (!token) return null;

    try {
      const data = await authFetch('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: token }),
      });
      authState.accessToken = data.accessToken;
      authState.refreshToken = data.refreshToken;
      authState.user = data.user;
      authState.companies = data.companies || [];
      persistRefreshToken(data.refreshToken);
      return data.accessToken;
    } catch {
      // Refresh failed — force logout
      authActions.clearSession();
      return null;
    }
  },

  /**
   * Log out (revoke server-side tokens + clear local state).
   */
  async logout(): Promise<void> {
    try {
      if (authState.accessToken) {
        await authFetch('/auth/logout', { method: 'POST' });
      }
    } catch {
      // Ignore logout errors
    }
    authActions.clearSession();
  },

  /**
   * Switch to a different company the user belongs to.
   * Returns the updated auth data for loadData to use.
   */
  async switchCompany(companyId: string): Promise<void> {
    authState.isSwitchingCompany = true;
    try {
      const data = await authFetch('/auth/switch-company', {
        method: 'POST',
        body: JSON.stringify({ companyId }),
      });
      authState.accessToken = data.accessToken;
      authState.refreshToken = data.refreshToken;
      authState.user = data.user;
      authState.companies = data.companies || [];
      persistRefreshToken(data.refreshToken);
    } finally {
      authState.isSwitchingCompany = false;
    }
  },

  // ─── TOTP (Authenticator-app) self-service (Playbook PR 8) ──────────────
  //
  // Wraps the /api/totp/* endpoints. Used by the "Authenticator App" card on
  // My Profile. Enrolment is a 3-step wizard: start (server returns a QR +
  // raw secret) → user scans → finish (user types first code; server persists
  // encrypted secret + returns 10 single-use recovery codes once).

  async getTotpStatus(): Promise<{
    enabled: boolean;
    enrolledAt: string | null;
    lastUsedAt: string | null;
    recoveryCodesRemaining: number;
  }> {
    return await authFetch('/totp/status');
  },

  async startTotpEnrollment(): Promise<{
    secret: string;
    otpauthUri: string;
    qrDataUrl: string;
  }> {
    return await authFetch('/totp/enroll/start', { method: 'POST', body: '{}' });
  },

  async finishTotpEnrollment(code: string): Promise<{
    enabled: boolean;
    recoveryCodes: string[];
  }> {
    return await authFetch('/totp/enroll/finish', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  async regenerateTotpRecoveryCodes(code: string): Promise<{ recoveryCodes: string[] }> {
    return await authFetch('/totp/regenerate-recovery-codes', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  async disableTotp(password: string): Promise<{ enabled: false }> {
    return await authFetch('/totp/disable', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  },

  // ─── WebAuthn / Passkeys (Playbook PR 9) ──────────────────────────────
  //
  // Thin wrappers around /api/webauthn/*. The actual WebAuthn ceremony
  // (calling the browser's `navigator.credentials.create/get`) is done in
  // the components that use these — they dynamically import
  // `@simplewebauthn/browser` so it stays out of the main bundle.

  async listPasskeys(): Promise<Array<{
    id: string;
    deviceName: string;
    lastUsedAt: string | null;
    createdAt: string;
    transports: string[];
  }>> {
    return await authFetch('/webauthn/credentials');
  },

  async startPasskeyRegistration(): Promise<unknown> {
    return await authFetch('/webauthn/register/options', {
      method: 'POST',
      body: '{}',
    });
  },

  async finishPasskeyRegistration(opts: {
    response: unknown;
    deviceName?: string;
  }): Promise<{ verified: boolean; deviceName: string }> {
    return await authFetch('/webauthn/register/verify', {
      method: 'POST',
      body: JSON.stringify(opts),
    });
  },

  async renamePasskey(id: string, deviceName: string): Promise<{ id: string; deviceName: string }> {
    return await authFetch(`/webauthn/credentials/${id}/rename`, {
      method: 'POST',
      body: JSON.stringify({ deviceName }),
    });
  },

  async deletePasskey(id: string): Promise<{ deleted: true }> {
    return await authFetch(`/webauthn/credentials/${id}`, { method: 'DELETE' });
  },

  /**
   * Pre-auth — start a passkey sign-in ceremony. Email is optional; when
   * omitted the browser shows every passkey for this rpID (discoverable
   * flow). Returns the WebAuthn options + a session UUID we must echo on
   * verify.
   */
  async startPasskeySignIn(email?: string): Promise<{ session: string } & Record<string, unknown>> {
    return await authFetch('/webauthn/authenticate/options', {
      method: 'POST',
      body: JSON.stringify(email ? { email } : {}),
    });
  },

  /**
   * Pre-auth — verify the browser's authentication response. On success the
   * server mints access + refresh tokens directly (no password, no 2FA),
   * which we commit to the local store the same way as a normal login.
   */
  async finishPasskeySignIn(opts: { session: string; response: unknown }): Promise<void> {
    const data = await authFetch('/webauthn/authenticate/verify', {
      method: 'POST',
      body: JSON.stringify(opts),
    });
    authState.accessToken = data.accessToken;
    authState.refreshToken = data.refreshToken;
    authState.user = data.user;
    authState.companies = data.companies || [];
    persistRefreshToken(data.refreshToken);
  },

  /**
   * Re-fetch the current user from the server and refresh the local cache.
   * Used after self-service profile edits (phone number etc.) so the menu
   * and downstream pages immediately reflect the change.
   */
  async refreshUser(): Promise<void> {
    if (!authState.accessToken) return;
    const data = await authFetch('/auth/me');
    authState.user = data.user;
    authState.companies = data.companies || [];
  },

  /**
   * Clear all auth state without calling the server.
   */
  clearSession(): void {
    authState.accessToken = null;
    authState.refreshToken = null;
    authState.user = null;
    authState.companies = [];
    persistRefreshToken(null);
  },
};
